import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { FaArrowLeftLong } from "react-icons/fa6";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import Swal from "sweetalert2";

import Button from "../../components/common/Button/Button";
import ReusableMuiTable from "../../components/common/Table/Table";
import FormInput from "../../components/common/FormInput/FormInput";
import { showToast } from "../../components/common/ToastNotification/ToastNotification";
import { getMileStone, addMilestone, getMilestoneById, updateMileStone, deleteMileStone } from "../../api/service/milestone.service";

const AddMileStone = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [mileStone, setMileStone] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");

  const [formValue, setFormValue] = useState({
    title: "",
    description: ""
  });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState({
    title: "",
    description: ""
  });

const fetchMilestones = useCallback(async () => {
  try {
    setLoading(true);

    const res = await getMileStone({
      page,
      limit: 10,
      search: searchText,
    });

    if (res?.success) {
      setMileStone(res.data.data || []);
      setTotalCount(res.data.total || 0);
    } else {
      showToast.error(res?.message || "Failed to load milestones");
    }
  } catch (error) {
    showToast.error(
      error?.response?.data?.message ||
      error?.message ||
      "Failed to load milestones"
    );
  } finally {
    setLoading(false);
  }
}, [page, searchText]);

  useEffect(() => {
    fetchMilestones();
  }, [fetchMilestones]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
    setPage(1); 
  };

  const handleEdit = async (row) => {
    try {
      setLoading(true);
      const res = await getMilestoneById(row.id);
      
      if (res?.success) {
        setFormValue({
          title: res.data.title || "",
          description: res.data.description || ""
        });
        setEditingId(row.id);
      } else {
        showToast.error(res?.message || "Failed to load milestone details");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to load milestone details";
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This milestone will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      const res = await deleteMileStone(row.id);

      if (res?.success) {
        showToast.success(res?.message || "Milestone deleted successfully");
        fetchMilestones();
      } else {
        Swal.fire("Error", res?.message || "Delete failed. Please try again.", "error");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "Delete failed. Please try again.";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const titleValue = formValue.title.trim();
    const descriptionValue = formValue.description.trim();

 
    let hasError = false;
    const newError = { title: "", description: "" };

    if (!titleValue) {
      newError.title = "Title is required";
      hasError = true;
    }

    if (!descriptionValue) {
      newError.description = "Description is required";
      hasError = true;
    }

    if (hasError) {
      setError(newError);
      return;
    }

    try {
      setLoading(true);

      let res;
      if (editingId) {
        res = await updateMileStone({
          milestone_id: editingId,
          title: titleValue,
          description: descriptionValue,
        });
      } else {
        res = await addMilestone({
          title: titleValue,
          description: descriptionValue,
        });
      }

      if (res?.success) {
        showToast.success(res?.message || `Milestone ${editingId ? 'updated' : 'added'} successfully`);

        setFormValue({ title: "", description: "" });
        setEditingId(null);
        setError({ title: "", description: "" });

        fetchMilestones();
      } else {
        showToast.error(res?.message || "Action failed");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "Action failed. Please try again.";
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      name: "title",
      label: "Year",
    },
    {
      name: "description",
      label: "Description",
    },
    {
      name: "actions",
      label: "Action",
      options: {
        sort: false,
        setCellProps: () => ({
          style: { textAlign: "right", width: 160 },
        }),
        customBodyRenderLite: (index) => {
          const row = mileStone[index];
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => handleEdit(row)}
                  sx={{ color: "#C39B22", p: 0.5 }}
                  disabled={loading}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  onClick={() => handleDelete(row)}
                  sx={{ color: "#d32f2f", p: 0.5 }}
                  disabled={loading}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  return (
    <div className="py-3">
      <div
        className="d-flex align-items-center gap-2 mb-3"
        style={{ cursor: "pointer" }}
        onClick={() => navigate(-1)}
      >
        <FaArrowLeftLong />
        <h3 className="mb-0 management-tile ml-2">
          Add / Edit Milestones
        </h3>
      </div>

      <Row className="mb-3">
        <Col xs={12}>
          <FormInput
            label="Title"
            value={formValue.title}
            onChange={(e) => {
              setFormValue({ ...formValue, title: e.target.value });
              setError({ ...error, title: "" });
            }}
            placeholder="Enter Year..."
            disabled={loading}
          />
          {error.title && <small className="text-danger">{error.title}</small>}
        </Col>

        <Col xs={12}>
          <FormInput
            type="textarea"
            label="Description"
            value={formValue.description}
            onChange={(e) => {
              setFormValue({ ...formValue, description: e.target.value });
              setError({ ...error, description: "" });
            }}
            placeholder="Enter description..."
            disabled={loading}
          />
          {error.description && <small className="text-danger">{error.description}</small>}
        </Col>

        <Col xs={12} md={3} className="mt-2">
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || (!formValue.title.trim() && !formValue.description.trim())}
          >
            {editingId ? "Update Milestone" : "Add Milestone"}
          </Button>
        </Col>
      </Row>

      <ReusableMuiTable
        title="Milestones"
        data={mileStone}
        columns={columns}
        loading={loading}
        options={{
          serverSide: true,
          page: page - 1,
          count: totalCount,
          rowsPerPage: 10,
          selectableRows: "none",
          search:true,
          searchText: searchText,
          searchPlaceholder: "Search team members...",
          onSearchChange: handleSearchChange,
          onChangePage: (p) => setPage(p + 1),
        }}
      />
    </div>
  );
};

export default AddMileStone;